import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PaperPlaneRight, Sparkle, Plus, LinkSimple, WarningCircle, Robot } from "@phosphor-icons/react";
import { toast } from "sonner";

const sentimentClasses = {
  positive: "bg-emerald-50 border-emerald-200 text-emerald-700",
  neutral: "bg-slate-50 border-slate-200 text-slate-600",
  negative: "bg-red-50 border-red-200 text-red-700",
};
const urgencyClasses = {
  low: "bg-slate-50 border-slate-200 text-slate-600",
  medium: "bg-amber-50 border-amber-200 text-amber-700",
  high: "bg-red-50 border-red-200 text-red-700",
};

export default function Inbox() {
  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [activePhone, setActivePhone] = useState(null);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState("");

  const [simOpen, setSimOpen] = useState(false);
  const [simPhone, setSimPhone] = useState("");
  const [simBody, setSimBody] = useState("");

  const [mapOpen, setMapOpen] = useState(false);
  const [mapClientId, setMapClientId] = useState("");
  const [mapDisplayName, setMapDisplayName] = useState("");
  const [newClientMode, setNewClientMode] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  const [analyzing, setAnalyzing] = useState(null);

  const load = async () => {
    const [m, c, cl] = await Promise.all([api.get("/messages"), api.get("/contacts"), api.get("/clients")]);
    setMessages(m.data); setContacts(c.data); setClients(cl.data);
  };
  useEffect(() => { load(); }, []);

  const threads = useMemo(() => {
    const map = new Map();
    for (const m of messages) {
      const existing = map.get(m.contact_phone);
      if (!existing || new Date(m.created_at) > new Date(existing.created_at)) {
        map.set(m.contact_phone, m);
      }
    }
    return Array.from(map.values()).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [messages]);

  const contactMap = Object.fromEntries(contacts.map(c => [c.phone, c]));
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));

  const activeContact = activePhone ? contactMap[activePhone] : null;
  const activeClient = activeContact?.client_id ? clientMap[activeContact.client_id] : null;
  const isUnmapped = activePhone && !activeClient;

  const loadThread = async (phone) => {
    setActivePhone(phone);
    const r = await api.get(`/messages/thread/${encodeURIComponent(phone)}`);
    setThread(r.data);
    load();
  };

  const send = async () => {
    if (!reply.trim() || !activePhone) return;
    await api.post(`/messages/reply/${encodeURIComponent(activePhone)}`, { body: reply });
    setReply("");
    toast.success("Reply sent (simulated WhatsApp)");
    loadThread(activePhone);
  };

  const simulate = async () => {
    if (!simPhone || !simBody) return;
    await api.post("/messages/simulate-inbound", { phone: simPhone, body: simBody });
    toast.success("Inbound message simulated");
    setSimOpen(false); setSimBody("");
    load();
    if (activePhone === simPhone) loadThread(simPhone);
  };

  const openMapDialog = () => {
    setMapClientId(activeContact?.client_id || "");
    setMapDisplayName(activeContact?.display_name || "");
    setNewClientMode(false); setNewClientName("");
    setMapOpen(true);
  };

  const saveMapping = async () => {
    if (!activePhone) return;
    let clientId = mapClientId;
    if (newClientMode) {
      if (!newClientName.trim()) return;
      const r = await api.post("/clients", { name: newClientName.trim() });
      clientId = r.data.id;
    }
    if (!clientId) return;
    await api.post("/contacts/map", {
      phone: activePhone,
      client_id: clientId,
      display_name: mapDisplayName || null,
    });
    toast.success("Contact mapped to client");
    setMapOpen(false);
    await load();
    await loadThread(activePhone);
  };

  const analyzeWithAI = async (msgId) => {
    setAnalyzing(msgId);
    try {
      const r = await api.post(`/messages/${msgId}/analyze`);
      toast.success(`AI: ${r.data.sentiment} · ${r.data.urgency}`);
      // reflect updated labels in the local thread
      setThread(t => t.map(m => m.id === msgId ? { ...m, sentiment: r.data.sentiment, urgency: r.data.urgency } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI analysis failed");
    } finally { setAnalyzing(null); }
  };

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Shared inbox</div>
          <h1 className="mt-1 font-[Manrope] font-extrabold text-4xl tracking-tight">WhatsApp threads</h1>
          <p className="mt-2 text-slate-600">Urgency and sentiment are flagged by fast keyword rules — press <span className="font-mono text-slate-800">Analyze</span> on any message for a deeper AI pass.</p>
        </div>
        <Dialog open={simOpen} onOpenChange={setSimOpen}>
          <DialogTrigger asChild>
            <Button data-testid="simulate-inbound-btn" className="bg-[#0F172A] hover:bg-[#0F172A]/90 text-white">
              <Plus size={16} className="mr-1.5" /> Simulate inbound
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Simulate an inbound WhatsApp message</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="sim-phone" placeholder="+15551234567" value={simPhone} onChange={e => setSimPhone(e.target.value)} />
              <Textarea data-testid="sim-body" placeholder="Message body…" rows={4} value={simBody} onChange={e => setSimBody(e.target.value)} />
            </div>
            <DialogFooter>
              <Button data-testid="sim-send" onClick={simulate}>Add to inbox</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border border-slate-200 shadow-none rounded-md overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 text-xs uppercase font-mono tracking-widest text-slate-500">Threads</div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
            {threads.length === 0 && <div className="p-6 text-sm text-slate-500">No conversations yet.</div>}
            {threads.map(t => {
              const contact = contactMap[t.contact_phone];
              const client = contact?.client_id ? clientMap[contact.client_id] : null;
              const active = activePhone === t.contact_phone;
              const unmapped = !client;
              return (
                <button
                  key={t.contact_phone}
                  data-testid={`thread-${t.contact_phone}`}
                  onClick={() => loadThread(t.contact_phone)}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${active ? "bg-blue-50/50 border-l-2 border-blue-500" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm text-slate-900 truncate">{contact?.display_name || t.contact_phone}</div>
                    <div className="flex items-center gap-1.5">
                      {unmapped && (
                        <span data-testid={`unmapped-${t.contact_phone}`} className="text-[10px] font-medium px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700 inline-flex items-center gap-1">
                          <WarningCircle size={10} weight="fill" /> Unmapped
                        </span>
                      )}
                      {t.direction === "inbound" && !t.read && <span className="h-2 w-2 rounded-full bg-blue-500" />}
                    </div>
                  </div>
                  <div className="text-xs font-mono text-slate-500 mt-0.5 truncate">
                    {t.contact_phone}{client ? ` · ${client.name}` : ""}
                  </div>
                  <div className="text-sm text-slate-600 truncate mt-1">{t.body}</div>
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="lg:col-span-2 border border-slate-200 shadow-none rounded-md flex flex-col min-h-[70vh]">
          {activePhone ? (
            <>
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{activeContact?.display_name || activePhone}</div>
                  <div className="text-xs font-mono text-slate-500 truncate">
                    {activePhone}
                    {activeClient
                      ? <> · linked to <span className="text-slate-700">{activeClient.name}</span></>
                      : <span className="ml-2 text-amber-700">· not linked to any client</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-xs text-slate-500 font-mono">{thread.length} msg</div>
                  <Button
                    data-testid="map-contact-btn"
                    size="sm"
                    variant={isUnmapped ? "default" : "outline"}
                    onClick={openMapDialog}
                    className={isUnmapped ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
                  >
                    <LinkSimple size={14} className="mr-1.5" />
                    {isUnmapped ? "Map to client" : "Change mapping"}
                  </Button>
                </div>
              </div>

              {isUnmapped && (
                <div data-testid="unmapped-banner" className="px-5 py-2.5 border-b border-amber-200 bg-amber-50 text-amber-800 text-sm flex items-center gap-2">
                  <WarningCircle size={16} weight="fill" />
                  This number isn't mapped to a client yet. Map it so replies and flash reports are attributed correctly.
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50/40">
                {thread.map(m => (
                  <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-md px-3.5 py-2 border ${m.direction === "outbound" ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-slate-200"}`}>
                      <div className="text-sm leading-relaxed">{m.body}</div>
                      <div className={`mt-1.5 flex items-center gap-2 text-[10px] font-mono ${m.direction === "outbound" ? "text-white/60" : "text-slate-500"}`}>
                        <span>{new Date(m.created_at).toLocaleString()}</span>
                        {m.direction === "inbound" && (
                          <>
                            <span className={`px-1.5 py-0.5 rounded border ${sentimentClasses[m.sentiment || "neutral"]}`}>
                              <Sparkle size={9} weight="fill" className="inline mr-0.5" />{m.sentiment}
                            </span>
                            <span className={`px-1.5 py-0.5 rounded border ${urgencyClasses[m.urgency || "low"]}`}>{m.urgency}</span>
                            <button
                              data-testid={`analyze-${m.id}`}
                              onClick={() => analyzeWithAI(m.id)}
                              disabled={analyzing === m.id}
                              className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 hover:bg-white text-slate-600 hover:text-slate-900 transition-colors"
                              title="Re-analyze this message with AI"
                            >
                              <Robot size={10} weight="regular" />
                              {analyzing === m.id ? "…" : "Analyze"}
                            </button>
                          </>
                        )}
                        {m.author && <span>· {m.author}</span>}
                        {m.direction === "outbound" && m.whatsapp_status === "simulated" && (
                          <span data-testid={`sim-badge-${m.id}`} className="ml-1 px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700 text-[10px]">sim</span>
                        )}
                        {m.direction === "outbound" && m.whatsapp_status === "failed" && (
                          <span data-testid={`fail-badge-${m.id}`} className="ml-1 px-1.5 py-0.5 rounded border border-red-200 bg-red-50 text-red-700 text-[10px]">failed</span>
                        )}
                        {m.direction === "outbound" && m.whatsapp_status === "accepted" && (
                          <span data-testid={`sent-badge-${m.id}`} className="ml-1 px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px]">sent</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-200 p-3 flex gap-2">
                <Textarea data-testid="reply-input" rows={1} value={reply} onChange={e => setReply(e.target.value)}
                  placeholder="Type a reply…"
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
                <Button data-testid="reply-send" onClick={send} className="bg-[#0F172A] hover:bg-[#0F172A]/90 text-white">
                  <PaperPlaneRight size={16} weight="fill" />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex-1 grid place-items-center text-sm text-slate-500">Select a thread to view messages</div>
          )}
        </Card>
      </div>

      {/* Map dialog */}
      <Dialog open={mapOpen} onOpenChange={setMapOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{activeContact?.client_id ? "Change client mapping" : "Map this number to a client"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1.5">Phone</div>
              <div className="font-mono text-sm px-3 py-2 rounded-md border border-slate-200 bg-slate-50">{activePhone}</div>
            </div>

            {!newClientMode ? (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1.5">Client</div>
                <Select value={mapClientId} onValueChange={setMapClientId}>
                  <SelectTrigger data-testid="map-client-select"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  data-testid="new-client-toggle"
                  onClick={() => setNewClientMode(true)}
                  className="mt-2 text-xs font-medium text-blue-700 hover:text-blue-900"
                >
                  + Create new client instead
                </button>
              </div>
            ) : (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1.5">New client name</div>
                <Input data-testid="new-client-name" placeholder="Business name" value={newClientName} onChange={e => setNewClientName(e.target.value)} />
                <button
                  type="button"
                  data-testid="pick-existing-toggle"
                  onClick={() => setNewClientMode(false)}
                  className="mt-2 text-xs font-medium text-slate-600 hover:text-slate-900"
                >
                  ← Pick an existing client
                </button>
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1.5">Display name (optional)</div>
              <Input data-testid="map-display-name" placeholder="Jane (Acme)" value={mapDisplayName} onChange={e => setMapDisplayName(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMapOpen(false)}>Cancel</Button>
            <Button data-testid="map-save-btn" onClick={saveMapping}>Save mapping</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
